/**
 * Web ``AndoStorage`` adapter — wraps ``localStorage`` with JSON
 * (de)serialisation and a Promise-returning API so the same call-sites work
 * with the Tauri ``plugin-store`` adapter.
 *
 * Falls back to an in-memory map in SSR / Node environments where
 * ``localStorage`` is unavailable (Next.js RSC).
 */

import type { AndoStorage } from "./types.js";

function getBackend(): Pick<Storage, "getItem" | "setItem" | "removeItem" | "clear"> {
  if (typeof window !== "undefined" && typeof window.localStorage !== "undefined") {
    return window.localStorage;
  }
  // SSR / Node fallback — keeps types honest without crashing imports.
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
    clear: () => {
      map.clear();
    },
  };
}

export class WebStorage implements AndoStorage {
  private backend = getBackend();

  async get<T>(key: string): Promise<T | null> {
    const raw = this.backend.getItem(key);
    if (raw === null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      // Legacy plain-string entries — return as-is.
      return raw as unknown as T;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.backend.setItem(key, JSON.stringify(value));
  }

  async remove(key: string): Promise<void> {
    this.backend.removeItem(key);
  }

  async clear(): Promise<void> {
    this.backend.clear();
  }
}
